import InstallmentDataCard from './components/InstallmentDataCard';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Conditional, Text } from '@consumidor-positivo/aurora';
import { useDeviceSize } from '@libs/utils/hooks/useDeviceSize';
import { OfferFeatured } from '@hub/components/OfferFeatured';
import { pageNames } from '@common/routes/pageNames';
import UpdateContactData from '../UpdateContactData';
import OrientationCard from '../OrientationCard';
import HeadingSection from '../HeadingSection';
import { DebtData } from '@common/hooks/useDebts/types';
import { DebtBankAccount } from '@common/apis/simulated-debt/types';
import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';
import { CustomExperienceService } from '@libs/renegkit';
import * as itau from '@dues/helpers/itau';

import './styles.scss';

interface DebitAccountProps {
  debtData: DebtData;
  firstInstallmentExpDate: string;
  handleSeeAgreementDetails: () => void;
  selectedBankAccount: DebtBankAccount | itau.BankAccounts | undefined;
  handleUpdateContactData: () => void;
}

const DebitAccount = ({
  debtData,
  firstInstallmentExpDate,
  handleSeeAgreementDetails,
  selectedBankAccount,
  handleUpdateContactData,
}: DebitAccountProps) => {
  const { isMobile } = useDeviceSize();
  const isTablet = window.innerWidth >= 720 && window?.innerWidth < 1024;
  const navigate = useNavigate();

  //#region ACP
  const { authenticatedUser, tokenOrigin } = useAuthenticatedUser();
  const isNCADCheckout = authenticatedUser?.customer?.required_updates?.includes('account_ncad');
  const isSimplified = tokenOrigin() === 'simplified';
  //#region End ACP

  return (
    <>
      <HeadingSection hasDebitAccount debtData={debtData} firstInstallmentExpDate={firstInstallmentExpDate} />

      <div className="debit-account-page__container">
        <div className="debit-account-page__header">
          <Text variant="heading-small" weight="bold">
            Detalhes do acordo
          </Text>
          <Conditional
            condition={!isMobile}
            renderIf={
              <Button expand="x" type="outlined" onClick={handleSeeAgreementDetails}>
                Ver detalhes do acordo
              </Button>
            }
          />
        </div>

        <Conditional condition={!isMobile && !isTablet} renderIf={<div className="closed-agreement-page__divider" />} />

        <Conditional
          condition={!isNCADCheckout}
          renderIf={
            <div className="closed-agreement-page__alert">
              <Alert
                type={2}
                status="warning"
                {...(isMobile && {
                  actionButton: { content: 'Atualizar dados', onClick: handleUpdateContactData },
                })}
              >
                <Text>Atualize seus dados para receber avisos de parcelas e vencimentos.</Text>
              </Alert>
            </div>
          }
        />

        <div className="debit-account-page__content">
          <div className="debit-account-page__aside-content">
            <OrientationCard pix={false} hasDebitAccount />

            <div className="debit-account-page__aside-content--bottom">
              <Conditional
                condition={!!selectedBankAccount}
                renderIf={
                  <div className="debit-account-page__bank-data">
                    <Text variant="heading-micro" weight="bold">
                      Conta cadastrada
                    </Text>
                    <div className="debit-account-page__card-line">
                      <Text color="secondary">Banco:</Text>
                      <Text color="secondary" weight="bold">
                        {selectedBankAccount?.bank}
                      </Text>
                    </div>
                    <div className="closed-agreement-page__divider" />

                    <div className="debit-account-page__card-line">
                      <Text color="secondary">Agência:</Text>
                      <Text color="secondary" weight="bold">
                        {selectedBankAccount?.account}
                      </Text>
                    </div>
                    <div className="closed-agreement-page__divider" />

                    <div className="debit-account-page__card-line">
                      <Text color="secondary">Conta:</Text>
                      <Text color="secondary" weight="bold">
                        {selectedBankAccount?.agency}
                      </Text>
                    </div>
                  </div>
                }
              />

              <Conditional condition={isMobile && !isNCADCheckout && !isSimplified} renderIf={<UpdateContactData partnerLogo={debtData.display.partnerImage} />} />

              <Conditional
                condition={!isNCADCheckout}
                renderIf={<InstallmentDataCard firstInstallmentExpDate={firstInstallmentExpDate} debtData={debtData} />}
              />
            </div>
          </div>
          <Conditional condition={!isMobile && !isNCADCheckout && !isSimplified} renderIf={<UpdateContactData partnerLogo={debtData.display.partnerImage} />} />
        </div>

        <Conditional
          condition={!!isMobile}
          renderIf={
            <Button expand="x" type="outlined" onClick={handleSeeAgreementDetails}>
              Ver detalhes do acordo
            </Button>
          }
        />
        <Conditional
          condition={!CustomExperienceService.getLeadsCustomExperience() && !isNCADCheckout}
          renderIf={
            <div className="debit-account-page__offers">
              <OfferFeatured
                forceDecide
                location="ac-acordo-fechado-destaque"
                blockTitle="Você desbloqueou novas ofertas!"
                onClickOffersCTA={() => navigate(pageNames.offers.path)}
              />
            </div>
          }
        />
      </div>
    </>
  );
};

export default DebitAccount;
